import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Download as DownloadIcon, Science as ScienceIcon } from '@mui/icons-material';

/**
 * Sticky toolbar for image-grid multi-select on the observation-image pages.
 *
 * Standalone (does not reuse SelectionBar, which hides at zero selection) so the
 * "Select all" affordance is reachable from an empty grid. Buttons only — download
 * progress is rendered by the host page's own download UI.
 *
 * @param {object}   props.sel                the useImageSelection() return
 * @param {boolean}  props.busy               a subset download is in flight (disables downloads)
 * @param {function} props.onDownloadRaw      download the selected raw frames
 * @param {function} props.onDownloadCalibrated download the calibratable selected frames
 */
export default function ImageSelectionBar({ sel, busy, onDownloadRaw, onDownloadCalibrated }) {
  const { selectedCount, calibratableCount, selectableCount, allSelected, selectAll, clear } = sel;

  return (
    <Box
      data-testid="selection-bar"
      sx={{
        position: 'sticky', top: 0, zIndex: 3,
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        px: 2, py: 1, mb: 2,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1,
      }}
    >
      <Button size="small" onClick={selectAll} disabled={allSelected || selectableCount === 0}>
        Select all ({selectableCount})
      </Button>

      {selectedCount > 0 && (
        <>
          <Typography variant="body2" color="text.secondary">
            {selectedCount} selected
          </Typography>
          <Button size="small" onClick={clear}>Clear</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={busy}
            onClick={onDownloadRaw}
          >
            Download Raw ({selectedCount})
          </Button>
          {calibratableCount > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<ScienceIcon />}
              disabled={busy}
              onClick={onDownloadCalibrated}
            >
              Download Calibrated ({calibratableCount})
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
